import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Nav from "@/components/Nav";

/** Admin-only page combining two related flows (iter-41):
 *
 *  1. **Grant free exports by email** — allowlist. Adding a row here does
 *     NOT create an account; it tags an email address so that when its
 *     owner registers (or if they already have an account) they unlock
 *     PDF / DOCX exports for free. Now includes a "Copy signup link"
 *     button per row so admins can share a pre-filled registration URL.
 *  2. **Create an account now** — direct admin-provisioned user creation
 *     with email + password. Automatically added to the allowlist so
 *     the new account can export for free immediately. Admin gives the
 *     credentials to the invitee; they can rotate the password later.
 *
 *  Access is gated client-side; the backend also enforces role=admin on
 *  every endpoint. */
export default function AdminFreeUsers() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [emails, setEmails] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // Create-user form state.
  const [cuEmail, setCuEmail] = useState("");
  const [cuPassword, setCuPassword] = useState("");
  const [cuName, setCuName] = useState("");
  const [cuNote, setCuNote] = useState("");
  const [cuBusy, setCuBusy] = useState(false);
  const [cuResult, setCuResult] = useState(null); // {email, password, note} of last created user
  // iter-42: pending password-reset tokens visible to admin so they
  // can copy the reset link and share it manually until Resend is wired.
  const [resets, setResets] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/free-users");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setItems([]);
    }
    // Fire-and-forget: pending reset tokens. Failure is silent so a
    // legacy backend without this endpoint doesn't break the page.
    try {
      const { data } = await api.get("/admin/password-resets");
      setResets(data?.entries || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);
  // Poll every 30s so admins see fresh reset requests without a manual refresh.
  useEffect(() => {
    if (!user) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user, load]);

  if (!user) return <Navigate to="/login" replace />;
  if ((user.role || "").toLowerCase() !== "admin") {
    return (
      <div style={{padding:"48px", textAlign:"center"}}>
        <Nav />
        <p style={{marginTop:120, color:"#7a7a72"}}>Admin access required.</p>
      </div>
    );
  }

  const addToAllowlist = async (e) => {
    e.preventDefault();
    const list = emails.split(/[\s,;]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    if (!list.length) { toast.error("Enter at least one email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/free-users/bulk", { emails: list, note });
      toast.success(`Added / updated ${data.added} email(s)`);
      setEmails(""); setNote("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const revoke = async (email) => {
    if (!window.confirm(`Revoke free-access for ${email}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/free-users/${encodeURIComponent(email)}`);
      toast.success("Revoked");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    const email = cuEmail.trim().toLowerCase();
    if (!email) return toast.error("Email is required");
    if (cuPassword.length < 6) return toast.error("Password must be at least 6 characters");
    setCuBusy(true);
    try {
      await api.post("/admin/users", {
        email, password: cuPassword, name: cuName.trim() || null, role: "user",
      });
      // Also tag them for free exports so the whole "give them a working
      // account" flow completes in one shot.
      await api.post("/admin/free-users/bulk", {
        emails: [email], note: cuNote || note || "Provisioned by admin",
      });
      toast.success(`Account created for ${email}`);
      setCuResult({ email, password: cuPassword, note: cuNote });
      setCuEmail(""); setCuPassword(""); setCuName(""); setCuNote("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCuBusy(false); }
  };

  // Build a pre-filled signup URL for the "share link" flow — an invitee
  // who opens this URL lands on /register with the email prefilled and
  // just picks a password.
  const signupLinkFor = (email) => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/register?email=${encodeURIComponent(email)}`;
  };

  const copyToClipboard = async (text, label = "Link") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      // Fallback for older browsers / non-secure contexts.
      window.prompt("Copy this link:", text);
    }
  };

  const active = (items || []).filter(x => x.is_active);
  const revoked = (items || []).filter(x => !x.is_active);

  return (
    <div style={{minHeight:"100vh", background:"#f6f7f7", fontFamily:"Inter, system-ui, sans-serif"}}>
      <Nav />
      <div style={{maxWidth:900, margin:"32px auto", padding:"0 24px"}} data-testid="admin-free-users-page">
        <h1 style={{fontSize:28, margin:0}}>Free Access & User Provisioning</h1>
        <p style={{color:"#5c5c58", marginTop:6, lineHeight:1.5}}>
          Two flows below — pick the one that matches your situation.
        </p>

        {/* Flow 1: grant free exports by email (allowlist). */}
        <section style={{marginTop:24}}>
          <h2 style={{fontSize:18, margin:0}}>1. Grant free exports by email</h2>
          <p style={{color:"#5c5c58", marginTop:4, fontSize:13, lineHeight:1.5}}>
            <strong>Adding an email here does <u>not</u> create an account.</strong>
            &nbsp;It tags the address so that whenever its owner registers on
            BookForge (or if they already have an account), they unlock every
            PDF / DOCX export without paying. Use the "Copy signup link"
            button below to share a pre-filled registration URL with the
            person.
          </p>
          <form onSubmit={addToAllowlist} style={{background:"#fff", border:"1px solid #dcdcde", padding:20, marginTop:12}} data-testid="admin-add-form">
            <label style={{display:"block", fontSize:12, fontWeight:600, color:"#5c5c58"}}>Emails</label>
            <textarea
              rows={4}
              value={emails}
              onChange={(e)=>setEmails(e.target.value)}
              placeholder="richard@example.com, mary@example.com&#10;team@studio.com"
              data-testid="admin-emails-input"
              style={{width:"100%", padding:"8px 10px", fontFamily:"ui-monospace, SFMono-Regular, monospace"}}
            />
            <label style={{display:"block", fontSize:12, fontWeight:600, color:"#5c5c58", marginTop:12}}>Note (optional)</label>
            <input
              type="text"
              value={note}
              onChange={(e)=>setNote(e.target.value)}
              placeholder="Founding member, beta tester, VIP customer…"
              data-testid="admin-note-input"
              style={{width:"100%", padding:"8px 10px"}}
            />
            <div style={{marginTop:12, display:"flex", gap:10, alignItems:"center"}}>
              <button className="button button-primary" type="submit" disabled={busy} data-testid="admin-add-submit">
                {busy ? "Saving…" : "Add / update"}
              </button>
              <span style={{fontSize:12, color:"#7a7a72"}}>
                Existing emails get their note refreshed and are re-activated.
              </span>
            </div>
          </form>
        </section>

        {/* Flow 2: create a real user account with a password. */}
        <section style={{marginTop:32}}>
          <h2 style={{fontSize:18, margin:0}}>2. Create an account now (with a temporary password)</h2>
          <p style={{color:"#5c5c58", marginTop:4, fontSize:13, lineHeight:1.5}}>
            Provision a working login immediately. The new user is added
            to the free-access list automatically, so they can export as
            soon as they sign in. Share the credentials with them — they
            can rotate the password from their profile later.
          </p>
          <form onSubmit={createUser} style={{background:"#fff", border:"1px solid #dcdcde", padding:20, marginTop:12}} data-testid="admin-create-user-form">
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12}}>
              <label style={{display:"block"}}>
                <span style={{display:"block", fontSize:12, fontWeight:600, color:"#5c5c58"}}>Email</span>
                <input type="email" value={cuEmail} onChange={(e)=>setCuEmail(e.target.value)}
                  placeholder="alice@example.com" required
                  data-testid="admin-create-email"
                  style={{width:"100%", padding:"8px 10px", marginTop:4, fontFamily:"ui-monospace, SFMono-Regular, monospace"}}/>
              </label>
              <label style={{display:"block"}}>
                <span style={{display:"block", fontSize:12, fontWeight:600, color:"#5c5c58"}}>Temporary password (min 6)</span>
                <input type="text" value={cuPassword} onChange={(e)=>setCuPassword(e.target.value)}
                  placeholder="e.g. Welcome-24!"
                  required minLength={6}
                  data-testid="admin-create-password"
                  style={{width:"100%", padding:"8px 10px", marginTop:4, fontFamily:"ui-monospace, SFMono-Regular, monospace"}}/>
              </label>
              <label style={{display:"block"}}>
                <span style={{display:"block", fontSize:12, fontWeight:600, color:"#5c5c58"}}>Display name (optional)</span>
                <input type="text" value={cuName} onChange={(e)=>setCuName(e.target.value)}
                  placeholder="Alice Cooper"
                  data-testid="admin-create-name"
                  style={{width:"100%", padding:"8px 10px", marginTop:4}}/>
              </label>
              <label style={{display:"block"}}>
                <span style={{display:"block", fontSize:12, fontWeight:600, color:"#5c5c58"}}>Note (optional)</span>
                <input type="text" value={cuNote} onChange={(e)=>setCuNote(e.target.value)}
                  placeholder="Reason / team name"
                  data-testid="admin-create-note"
                  style={{width:"100%", padding:"8px 10px", marginTop:4}}/>
              </label>
            </div>
            <div style={{marginTop:12, display:"flex", gap:10, alignItems:"center"}}>
              <button className="button button-primary" type="submit" disabled={cuBusy} data-testid="admin-create-submit">
                {cuBusy ? "Creating…" : "Create account + grant free access"}
              </button>
              <span style={{fontSize:12, color:"#7a7a72"}}>
                Password is shown to you once so you can share it with the user.
              </span>
            </div>
          </form>

          {cuResult && (
            <div style={{marginTop:12, background:"#eef7ee", border:"1px solid #b6d7b6", padding:14}} data-testid="admin-create-result">
              <p style={{margin:0, fontSize:13}}><strong>Account created — share these with {cuResult.email}:</strong></p>
              <div style={{marginTop:6, fontFamily:"ui-monospace, SFMono-Regular, monospace", fontSize:13}}>
                Email: {cuResult.email}<br/>
                Password: {cuResult.password}
              </div>
              <div style={{marginTop:8, display:"flex", gap:8}}>
                <button
                  className="button"
                  onClick={()=>copyToClipboard(
                    `BookForge login\nEmail: ${cuResult.email}\nPassword: ${cuResult.password}\nSign in: ${window.location.origin}/login`,
                    "Credentials"
                  )}
                  style={{fontSize:12, padding:"3px 10px"}}
                  data-testid="admin-create-copy-credentials"
                >Copy credentials</button>
                <button
                  className="button"
                  onClick={()=>setCuResult(null)}
                  style={{fontSize:12, padding:"3px 10px"}}
                  data-testid="admin-create-result-dismiss"
                >Dismiss</button>
              </div>
            </div>
          )}
        </section>

        {/* Flow 3: pending password reset requests (iter-42) — admin-copy mode. */}
        <section style={{marginTop:32}}>
          <h2 style={{fontSize:18, margin:0}}>3. Pending password reset requests</h2>
          <p style={{color:"#5c5c58", marginTop:4, fontSize:13, lineHeight:1.5}}>
            Users who clicked "Forgot password?" on the login page show up
            here with a one-time secure link (60 min expiry). Copy the link
            and share it with them — auto-email delivery via Resend is
            coming as soon as an API key is added.
          </p>
          {resets.length === 0 ? (
            <p style={{color:"#7a7a72", fontSize:13, marginTop:8, fontStyle:"italic"}} data-testid="admin-resets-empty">
              No pending reset requests.
            </p>
          ) : (
            <table className="wp-list-table widefat striped" data-testid="admin-resets-table"
                    style={{background:"#fff", border:"1px solid #dcdcde", marginTop:8}}>
              <thead><tr>
                <th>Email</th><th>Requested</th><th>Expires</th>
                <th style={{width:120}}>Status</th><th style={{width:150}}></th>
              </tr></thead>
              <tbody>
                {resets.map((r) => {
                  const url = typeof window !== "undefined"
                    ? `${window.location.origin}/reset-password?token=${encodeURIComponent(r.token)}`
                    : "";
                  const used = !!r.used_at;
                  return (
                    <tr key={r.token} data-testid={`admin-reset-row-${r.email}`}>
                      <td style={{fontFamily:"ui-monospace, SFMono-Regular, monospace"}}>{r.email}</td>
                      <td style={{fontSize:12, color:"#5c5c58"}}>{(r.created_at || "").slice(0, 16).replace("T"," ")}</td>
                      <td style={{fontSize:12, color:"#5c5c58"}}>{(r.expires_at || "").slice(0, 16).replace("T"," ")}</td>
                      <td>
                        <span style={{
                          fontSize:11, padding:"2px 8px",
                          background: used ? "#f0f0f1" : "#eef7ee",
                          color: used ? "#7a7a72" : "#2e7d32",
                        }}>{used ? "Used" : "Pending"}</span>
                      </td>
                      <td>
                        {!used && (
                          <button className="button"
                            onClick={()=>copyToClipboard(url, "Reset link")}
                            data-testid={`admin-reset-copy-${r.email}`}
                            style={{fontSize:12, padding:"2px 8px"}}>Copy reset link</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>

        {/* Existing allowlist table. */}
        <h2 style={{marginTop:32, fontSize:20}}>Active on the allowlist ({active.length})</h2>
        {items === null && <p style={{color:"#7a7a72"}}>Loading…</p>}
        {items && active.length === 0 && <p style={{color:"#7a7a72"}}>No emails added yet.</p>}
        {active.length > 0 && (
          <table className="wp-list-table widefat striped" data-testid="admin-active-table" style={{background:"#fff", border:"1px solid #dcdcde"}}>
            <thead><tr><th>Email</th><th>Note</th><th>Added</th><th style={{width:210}}></th></tr></thead>
            <tbody>
              {active.map(x => (
                <tr key={x.email} data-testid={`admin-row-${x.email}`}>
                  <td style={{fontFamily:"ui-monospace, SFMono-Regular, monospace"}}>{x.email}</td>
                  <td>{x.note || <span style={{color:"#8c8f94"}}>—</span>}</td>
                  <td style={{fontSize:12, color:"#5c5c58"}}>{(x.added_at || "").slice(0, 10)}</td>
                  <td style={{whiteSpace:"nowrap"}}>
                    <button className="button" onClick={()=>copyToClipboard(signupLinkFor(x.email), "Signup link")}
                      title="Send this link to the user — the email will be prefilled on the register page so they only need to set a password."
                      data-testid={`admin-copy-link-${x.email}`} style={{fontSize:12, padding:"2px 8px", marginRight:6}}>Copy signup link</button>
                    <button className="button delete" onClick={()=>revoke(x.email)} disabled={busy}
                      data-testid={`admin-revoke-${x.email}`} style={{fontSize:12, padding:"2px 8px"}}>Revoke</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {revoked.length > 0 && (
          <>
            <h2 style={{marginTop:32, fontSize:20, color:"#8c8f94"}}>Revoked ({revoked.length})</h2>
            <ul style={{color:"#8c8f94", fontFamily:"ui-monospace, monospace", fontSize:12}}>
              {revoked.map(x => <li key={x.email}>{x.email}</li>)}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
